// 現在のアクター（識別情報）を返すフック。
//
// authMode === "mock"   : ローカル mock 利用者を即時返す
// authMode === "required": 認証済み ApiRequest で /api/me を取得し、
//                          TanStack Query でキャッシュする（fetch 中はフォールバック値を返す）
//
// MEIDEN CONNECT のセッション SDK の hook を使う設計の素地は残しつつ、
// /api/me を叩く方が API レイヤの actor 情報と整合する。

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiRequest } from "./api-request-context";
import { getWebConfig } from "../env";

export interface CurrentActor {
  readonly userId: string;
  readonly displayName: string;
  readonly avatarImageUrl?: string | undefined;
  readonly color: string;
}

interface MeResponse {
  actor: {
    userId: string;
    displayName: string;
    avatarImageUrl?: string | undefined;
  };
}

const MOCK_ACTOR: CurrentActor = {
  userId: "mock-canvas-user",
  displayName: "Mock 利用者",
  color: "#1e63d4",
};

// 名前から決定論的に色を割り当てる（プレゼンス UI の識別補助）。
function pickActorColor(userId: string): string {
  const palette = ["#1e63d4", "#d22b2b", "#2da55a", "#9b59b6", "#e67e22", "#16a085"];
  let hash = 0;
  for (let i = 0; i < userId.length; i += 1) {
    hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length] ?? "#1a1a2e";
}

export function useCurrentActor(): CurrentActor {
  const config = getWebConfig();
  const apiRequest = useApiRequest();
  const { data } = useQuery({
    queryKey: ["currentActor", config.authMode, config.apiBaseUrl] as const,
    enabled: config.authMode === "required",
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<CurrentActor> => {
      const response = await apiRequest("/me");
      if (!response.ok) {
        throw new Error(`/api/me ${response.status}`);
      }
      const json = (await response.json()) as MeResponse;
      return {
        userId: json.actor.userId,
        displayName: json.actor.displayName,
        avatarImageUrl: json.actor.avatarImageUrl,
        color: pickActorColor(json.actor.userId),
      };
    },
  });

  return useMemo(() => {
    if (config.authMode === "mock") return MOCK_ACTOR;
    if (data) return data;
    // 取得中は仮の actor で動作させる（カーソル broadcast などの即時動作を確保）
    return {
      userId: "loading",
      displayName: "...",
      color: "#999999",
    };
  }, [config.authMode, data]);
}
